// client/src/components/pages/Geofencing/purposeTypes.js
// LexiStar geofence PURPOSE types (contracts m1-contracts.md §2.3.1, LS-04 §5)
// with the LS-09 §4 default fills, and the mapping to/from the legacy
// `zone_type` taxonomy in ./zoneTypes (which still drives the map layer chips).
// Mirrors server/services/geofenceService.js — keep the two in step.
import React from 'react';

// `short` = badge label where `label` spells out the default role.
// `fill` = LS-09 §4 default fill; `pattern` = how the swatch draws it
// ('solid' | 'hatch' red diagonal | 'outline' neutral white outline | 'dotted'
// red dotted outline, no fill). `roles` = may carry loading / offloading roles.
// Site is not in the LS-09 fill table: it keeps the site-boundary sky blue.
export const PURPOSE_TYPES = [
  { id: 'site', label: 'Site', fill: '#0ea5e9', pattern: 'solid', roles: true },
  {
    id: 'source',
    label: 'Source (loading)',
    short: 'Source',
    fill: '#22c55e',
    pattern: 'solid',
    roles: true,
  },
  {
    id: 'destination',
    label: 'Destination (offloading)',
    short: 'Destination',
    fill: '#f59e0b',
    pattern: 'solid',
    roles: true,
  },
  { id: 'weighbridge', label: 'Weighbridge', fill: '#14b8a6', pattern: 'solid' },
  { id: 'delay', label: 'Delay', fill: '#f97316', pattern: 'solid' },
  { id: 'maintenance', label: 'Maintenance / workshop', fill: '#a855f7', pattern: 'solid' },
  { id: 'no_go', label: 'No-go', fill: '#ef4444', pattern: 'hatch' },
  { id: 'depot', label: 'Depot / yard', fill: '#cbd5e1', pattern: 'solid' },
  { id: 'fuel_station', label: 'Fuel station', fill: '#06b6d4', pattern: 'solid' },
  { id: 'rest_stop', label: 'Rest / safe stop', fill: '#cbd5e1', pattern: 'solid' },
  { id: 'speed_zone', label: 'Speed zone', fill: '#ffffff', pattern: 'outline' },
  { id: 'tipping_zone', label: 'Tipping zone', fill: '#f59e0b', pattern: 'solid' },
  { id: 'risk_zone', label: 'Risk zone', fill: '#ef4444', pattern: 'dotted' },
  { id: 'border_post', label: 'Border post', fill: '#f97316', pattern: 'solid' },
  {
    id: 'time_and_motion',
    label: 'Time-and-motion checkpoint',
    fill: '#ffffff',
    pattern: 'outline',
  },
  { id: 'custom', label: 'Custom', fill: '#6366f1', pattern: 'solid' },
];

export const PURPOSE_IDS = PURPOSE_TYPES.map((p) => p.id);
export const ROLE_PURPOSES = PURPOSE_TYPES.filter((p) => p.roles).map((p) => p.id);
export const DEFAULT_PURPOSE = 'source';

// Contract §2.3.1: legacy zone_type -> purpose_type (+ default roles).
export const ZONE_TO_PURPOSE = {
  site_boundary: { purposeType: 'site', loading: false, offloading: false },
  stockpile: { purposeType: 'source', loading: true, offloading: false },
  loading_bay: { purposeType: 'source', loading: true, offloading: false },
  weighbridge: { purposeType: 'weighbridge', loading: false, offloading: false },
  workshop: { purposeType: 'maintenance', loading: false, offloading: false },
  no_go: { purposeType: 'no_go', loading: false, offloading: false },
  custom: { purposeType: 'custom', loading: false, offloading: false },
};

// Inverse: which existing layer group (zone type chip) a purpose shows under.
export const PURPOSE_TO_ZONE = {
  site: 'site_boundary',
  source: 'stockpile',
  weighbridge: 'weighbridge',
  maintenance: 'workshop',
  no_go: 'no_go',
};

/** Layer group (legacy zone type id) for a purpose; unmapped purposes -> 'custom'. */
export const zoneTypeForPurpose = (purposeType) => PURPOSE_TO_ZONE[purposeType] || 'custom';

export const getPurposeMeta = (id) =>
  PURPOSE_TYPES.find((p) => p.id === id) || PURPOSE_TYPES.find((p) => p.id === 'custom');

/** Purpose of a geofence row: PurposeType when 021 is applied, else mapped from ZoneType. */
export const purposeOf = (g) =>
  g?.PurposeType || (ZONE_TO_PURPOSE[g?.ZoneType] || ZONE_TO_PURPOSE.stockpile).purposeType;

/** Default roles for a purpose (source -> loading, destination -> offloading). */
export const defaultRolesFor = (purposeType) => ({
  loading: purposeType === 'source',
  offloading: purposeType === 'destination',
});

/** Inline style drawing the LS-09 §4 fill for a purpose. */
export function swatchStyle(meta) {
  switch (meta.pattern) {
    case 'hatch':
      return {
        background: `repeating-linear-gradient(45deg, ${meta.fill} 0 2px, transparent 2px 4px)`,
        border: `1px solid ${meta.fill}`,
      };
    case 'outline':
      return { background: meta.fill, border: '1px solid #64748b' };
    case 'dotted':
      return { background: 'transparent', border: `2px dotted ${meta.fill}` };
    default:
      return { background: meta.fill, border: '1px solid rgba(15, 23, 42, 0.15)' };
  }
}

/** Small square showing a purpose's map fill. */
export function PurposeSwatch({ purpose, className = '' }) {
  const meta = getPurposeMeta(purpose);
  return (
    <span
      aria-hidden="true"
      data-testid={`purpose-swatch-${meta.id}`}
      className={`inline-block w-3 h-3 rounded-sm flex-shrink-0 ${className}`}
      style={swatchStyle(meta)}
    />
  );
}

/** Pill naming a purpose, with its swatch and any roles. */
export function PurposeBadge({ purpose, loading, offloading }) {
  const meta = getPurposeMeta(purpose);
  const roles = meta.roles
    ? [loading && 'loading', offloading && 'offloading'].filter(Boolean).join(' + ')
    : '';
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded-full whitespace-nowrap bg-slate-100 text-slate-700">
      <PurposeSwatch purpose={meta.id} />
      {meta.short || meta.label}
      {roles && <span className="text-slate-400 font-normal">· {roles}</span>}
    </span>
  );
}
